import { assertSafeInteger, assertSafeIntegerInRange } from '../integers';

/**
 * How many sockets a skin instance is born with (ADR 0040).
 *
 * ## What a socket capacity is
 *
 * An integer from 1 to 6, drawn ONCE by the server when the instance is
 * created, persisted on that instance, and never re-rolled. It is a property of
 * the COPY, not of the definition: two copies of the same skin legitimately
 * have different capacities, which is the whole reason rolling twice is
 * meaningful.
 *
 * Sockets have **no effect on the PvP**, which normalises combat to constants.
 * Jewels - the things that would eventually occupy a socket - do not exist, and
 * nothing in this file knows what one would be.
 *
 * ## Why the weights are integers, and why they sum to 10 000
 *
 * A draw decided by floating point is a draw whose odds cannot be stated
 * exactly. 25% is not representable in binary floating point, and neither are
 * 22%, 18%, 12% or 9% - so a comparison against `0.25` would hand out a
 * distribution nobody published, off in the last bits, forever.
 *
 * So the domain is the integers `[0, 10000)`, every band is a whole number of
 * points, and the published percentage is exact by construction: 2500/10000 IS
 * 25%, and there is no rounding anywhere between the entropy and the answer.
 *
 * Ten thousand is also basis points, which is the unit this codebase already
 * uses for every percentage, so a chance can be published without a second
 * convention.
 *
 * ## The profile is versioned, and the version is persisted
 *
 * `skin_socket_standard_v2` is a NAME for this exact table. An instance records
 * which profile and which version produced it, so a capacity drawn today stays
 * explicable after the table changes - the same reason a gacha pull records its
 * odds version. Changing a weight means a new version, never an edit.
 *
 * ## Why there is a v2 (gate V24A)
 *
 * v1 published `2500, 2200, 1800, 1500, 1100, 900`. The owner decided the
 * middle of the table differently: four sockets is 14% rather than 15%, and
 * five is 12% rather than 11%. The ends did not move - one socket is still 25%
 * and six is still 9%.
 *
 * That is one point moved from the fourth band to the fifth, and it was NOT
 * applied by editing the v1 array. A capacity is persisted next to the profile
 * that drew it precisely so that "why does this copy have four sockets" has an
 * answer years later; editing the table in place would make every v1 row cite a
 * table that no longer exists. No v1 row exists today - no banner has ever been
 * active - and the version was still bumped, because the rule is about what the
 * record MEANS, not about how many rows happen to be affected.
 */

/** The narrowest and widest a skin can ever be. Permanent. */
export const SKIN_SOCKET_MIN = 1;
export const SKIN_SOCKET_MAX = 6;

export type SkinSocketCapacity = 1 | 2 | 3 | 4 | 5 | 6;

/** Every capacity that exists, in order. */
export const SKIN_SOCKET_CAPACITIES: readonly SkinSocketCapacity[] = Object.freeze([
  1, 2, 3, 4, 5, 6,
]);

/** The profile's stable name and version. Both are persisted on every instance. */
export const SKIN_SOCKET_PROFILE_ID = 'skin_socket_standard_v2';
export const SKIN_SOCKET_PROFILE_VERSION = 2;

/**
 * The size of the draw's domain, in points.
 *
 * Also basis points, so a weight IS a chance: 2500 points is 2500 bps is 25%.
 */
export const SKIN_SOCKET_TOTAL_WEIGHT = 10_000;

/**
 * The weights, in the order of {@link SKIN_SOCKET_CAPACITIES}.
 *
 * 25% / 22% / 18% / 14% / 12% / 9%. The sum is checked below at module load
 * rather than trusted, because a table that does not sum to the domain is a
 * table with either a hole or an overlap in it, and both are silent.
 *
 * Nine hundred points for six sockets is a decision, not a leftover: the rarest
 * outcome is 9%, and it is not to be quietly reduced to make the top of the
 * table feel rarer.
 */
export const SKIN_SOCKET_WEIGHTS: readonly number[] = Object.freeze([
  2500, 2200, 1800, 1400, 1200, 900,
]);

/**
 * The half-open band each capacity owns, `[from, to)`, in points.
 *
 * Derived from the weights rather than written out, so the two can never
 * disagree. The bands are contiguous by construction: each begins exactly where
 * the previous ended.
 */
export interface SkinSocketBand {
  readonly sockets: SkinSocketCapacity;
  readonly weight: number;
  /** Inclusive. */
  readonly from: number;
  /** EXCLUSIVE. */
  readonly to: number;
  /** The published chance, in basis points. Exact: it is the weight. */
  readonly chanceBps: number;
}

function buildBands(): readonly SkinSocketBand[] {
  const bands: SkinSocketBand[] = [];
  let cursor = 0;
  for (const [index, sockets] of SKIN_SOCKET_CAPACITIES.entries()) {
    const weight = SKIN_SOCKET_WEIGHTS[index] as number;
    bands.push(
      Object.freeze({
        sockets,
        weight,
        from: cursor,
        to: cursor + weight,
        chanceBps: weight,
      }),
    );
    cursor += weight;
  }
  if (cursor !== SKIN_SOCKET_TOTAL_WEIGHT) {
    /*
     * Loud, at module load, and deliberately not a test.
     *
     * A table whose weights do not fill the domain leaves points that map to no
     * capacity (a hole) or to two (an overlap). Either would be a silently
     * wrong distribution, and it would ship. This makes the module refuse to
     * load instead.
     */
    throw new Error(`skin socket weights must sum to ${SKIN_SOCKET_TOTAL_WEIGHT}, got ${cursor}`);
  }
  return Object.freeze(bands);
}

export const SKIN_SOCKET_BANDS: readonly SkinSocketBand[] = buildBands();

/**
 * The capacity one uniform point in `[0, 10000)` selects.
 *
 * The entropy is supplied by the caller and is never produced here: this module
 * has no access to randomness of any kind, which is what makes it deterministic
 * and testable at every boundary. The server's economic source draws the point;
 * this decides what it means.
 *
 * A point outside the domain is a RangeError rather than a clamp. Clamping a
 * malformed value silently biases the result toward whichever end was clamped -
 * exactly the kind of quiet distortion `assertSafeIntegerInRange` exists to
 * prevent.
 */
export function socketCapacityAtPoint(point: number): SkinSocketCapacity {
  assertSafeIntegerInRange(point, 0, SKIN_SOCKET_TOTAL_WEIGHT - 1, 'socket draw point');
  for (const band of SKIN_SOCKET_BANDS) {
    if (point < band.to) {
      return band.sockets;
    }
  }
  // Unreachable while the sum check above holds; loud rather than a default.
  throw new Error(`no socket band contains point ${point}`);
}

/** The band a capacity owns, for publishing odds and for tests. */
export function socketBandFor(sockets: number): SkinSocketBand {
  assertSafeIntegerInRange(sockets, SKIN_SOCKET_MIN, SKIN_SOCKET_MAX, 'socket capacity');
  const band = SKIN_SOCKET_BANDS.find((entry) => entry.sockets === sockets);
  if (!band) {
    throw new Error(`no socket band for ${sockets}`);
  }
  return band;
}

/**
 * Whether a number is a capacity a skin instance may carry.
 *
 * Used by the service before it writes and by the wire before it answers. The
 * database says the same thing with a CHECK constraint, because a rule that
 * lives only in the application is a rule a direct write can miss.
 */
export function isSkinSocketCapacity(value: number): value is SkinSocketCapacity {
  return Number.isSafeInteger(value) && value >= SKIN_SOCKET_MIN && value <= SKIN_SOCKET_MAX;
}

export function assertSkinSocketCapacity(value: number, label = 'socket capacity'): number {
  assertSafeInteger(value, label);
  return assertSafeIntegerInRange(value, SKIN_SOCKET_MIN, SKIN_SOCKET_MAX, label);
}

/** `"25,00%"` for a weight, in the language the game speaks. Display only. */
export function socketChanceLabel(sockets: number): string {
  const bps = socketBandFor(sockets).chanceBps;
  const whole = Math.trunc(bps / 100);
  const fraction = bps % 100;
  return `${whole},${fraction.toString().padStart(2, '0')}%`;
}
